// ============================================================================
//  THE BREAD BOI UNIVERSE — character roster
// ============================================================================
// One source of truth for every character. The town spawns NPCs from this,
// buildings pull their shopkeepers from here, and Order Rush draws customers.
//
// This is also the character bible — the one place the whole cast is written
// down. Add a character by adding an entry; the game reads roles/flags to
// decide where (or whether) they appear.
//
// FIELD REFERENCE
//   id         unique slug, used as the spawn key everywhere
//   name       display name
//   bio        one line, shown when you talk to them
//   kind       what they physically are (bread | fruit | food | human | creature)
//   colors     palette hints for the 3D build { body, accent, ... } (hex ints)
//   role       'shopkeeper' | 'townsfolk' | 'band' | 'customer' | 'cameo'
//   building   which building they belong to, if any (matches town building ids)
//   wholesome  true = safe for the public game. false = excluded from spawns.
//   lines      idle chatter shown in speech bubbles
//   thanks     (customers) said when you serve their order
//
// KEEP IT WHOLESOME: breadtoasting.com is a public site anyone can wander onto.
// Anything mature-themed (pin-ups, the Reeferz vape line, smoking, etc.) is
// flagged wholesome:false and never spawns. The designs live on elsewhere in
// the brand; the game uses the family-friendly versions only.
// ============================================================================

export const CHARACTERS = {

  // ---- SHOPKEEPERS (one per building) ------------------------------------
  breadboi: {
    id:'breadboi', name:'Bread Boi', kind:'bread',
    bio:'the star of Toast Town. curious, versatile, a little mischievous.',
    colors:{ body:0xFFF4DF, crust:0x6E4526 },
    role:'shopkeeper', building:'house', wholesome:true,
    lines:['toast is self-care.', "let's build this town up!", '*happy bread noises*']
  },
  gary: {
    id:'gary', name:'Gary', kind:'creature',
    bio:'pink alien farmer chasing his dream to captain a pirate ship. writes comics.',
    colors:{ body:0xE8557F, eyes:0x111111 },
    role:'shopkeeper', building:'comics', wholesome:true,
    lines:['ever heard of the Dog-Pirates?', 'the Crovian System is wild.', 'someday: captain Gary.']
  },

  // ---- BAND (the café's live act) ----------------------------------------
  strawbella: {
    id:'strawbella', name:'Strawbella', kind:'fruit',
    bio:'pink yogurt of Dancing Fruit Yogurt. lead vocals & keys.',
    colors:{ body:0xE9A6C4 },
    role:'band', building:'cafe', wholesome:true,
    lines:['♪ ~ ♪', 'you feel that beat?']
  },
  blueberry: {
    id:'blueberry', name:'Blueberry', kind:'fruit',
    bio:'blue yogurt of Dancing Fruit Yogurt. bass & backing vocals.',
    colors:{ body:0x6C86D4 },
    role:'band', building:'cafe', wholesome:true,
    lines:['drop the bass. gently.', 'blue mood, good tunes.']
  },
  vanillio: {
    id:'vanillio', name:'Vanillio', kind:'fruit',
    bio:'pale yogurt of Dancing Fruit Yogurt. drums.',
    colors:{ body:0xF2E9C6 },
    role:'band', building:'cafe', wholesome:true,
    lines:['*drum fill*', 'one, two, one-two-three-four!']
  },

  // ---- TOWNSFOLK (wander the plaza, chat) --------------------------------
  sheriffberry: {
    id:'sheriffberry', name:'Sheriff Berry', kind:'fruit',
    bio:'a strawberry lawman in a ten-gallon hat. keeps the peace, mostly.',
    colors:{ body:0xD0342C, leaf:0x7FBF6A, hat:0xC89B6A },
    role:'townsfolk', building:null, wholesome:true,
    lines:["howdy, bread boi 🤠", "this town ain't big enough for two snacks", 'yeehaw.']
  },
  bullnana: {
    id:'bullnana', name:'Bull Nana', kind:'creature',
    bio:'a four-legged banana beast — part horse, part dog, part cow. speaks only moo.',
    colors:{ body:0xF2D23B, tips:0x6E4526 },
    role:'townsfolk', building:null, wholesome:true,
    quadruped:true,
    lines:['moo.', 'moo?', 'MOO!', '…moo (respectfully)']
  },
  footman: {
    id:'footman', name:'Foot Man', kind:'creature',
    bio:'an anxious five-toed foot who works an office job. means well.',
    colors:{ body:0xF3E0C4, brows:0x111111 },
    role:'townsfolk', building:null, wholesome:true,
    lines:['is this seat taken?', 'ugh, mondays.', '*nervous toe wiggle*']
  },
  kingbread: {
    id:'kingbread', name:'King Bread', kind:'bread',
    bio:'the crowned monarch of the bread realm. appears on special occasions.',
    colors:{ body:0xF3E3C4, robe:0xD0342C, crown:0xF7C948 },
    role:'townsfolk', building:null, wholesome:true, special:true,
    lines:['we are most pleased.', 'rise, loyal crumb.', 'the realm prospers.']
  },
  crsr: {
    id:'crsr', name:'CRSR', kind:'bread',
    bio:'toast in a snapback and a gold chain. always cooking up a deal.',
    colors:{ body:0xF3E3C4, jersey:0x3D5FC0, hat:0x35C0C8 },
    role:'townsfolk', building:null, wholesome:true,
    lines:['you want jawnz? i got jawnz.', 'crumbs make the world go round.', 'business is good.']
  },
  beatnik: {
    id:'beatnik', name:'Beatnik Bread', kind:'bread',
    bio:'beret, goatee, and a poem for every mood. holds down the open-mic.',
    colors:{ body:0xC9A876, beret:0x3D2E8C },
    role:'townsfolk', building:'cafe', wholesome:true,
    lines:['snap for the ambiance.', 'i wrote a haiku about crust.', 'dig it.']
  },
  coffeerocker: {
    id:'coffeerocker', name:'Coffee Rocker', kind:'bread',
    bio:'spiky hair, shades, and a bottomless mug. seen it all, toasted through it.',
    colors:{ body:0xC9A876, hair:0x6E4526 },
    role:'townsfolk', building:'cafe', wholesome:true,
    lines:['back in my day, toast was toast.', 'need a refill.', 'rock on, little slice.']
  },
  snailman: {
    id:'snailman', name:'Snail Man', kind:'creature',
    bio:'a snail with an oddly wise, weary face. takes life slow.',
    colors:{ body:0x8FC96B, shell:0xE9C46A },
    role:'townsfolk', building:null, wholesome:true,
    slow:true,
    lines:['…i will get there. eventually.', 'slow and steady.', '*contemplates*']
  },

  // ---- CUSTOMERS (seated regulars in Order Rush) -------------------------
  sourapple: {
    id:'sourapple', name:'Sour Apple', kind:'fruit',
    bio:'a green apple, permanently dazed and puckered. very sour, very sweet.',
    colors:{ body:0x9CCB3B, leaf:0x2E7D32 },
    role:'customer', building:null, wholesome:true,
    lines:['whoa… spinny.', 'sooour.'],
    thanks:['*wobbles happily*', 'sour but grateful!']
  },
  peanutman: {
    id:'peanutman', name:'Peanut Man', kind:'food',
    bio:'a stand-up peanut. shows up hungry, leaves happy.',
    colors:{ body:0xD9A441 },
    role:'customer', building:null, wholesome:true,
    lines:['smells good in here.', 'table for one?'],
    thanks:['nutty and nice!', 'much appreciated.']
  },
  juiceboxgirl: {
    id:'juiceboxgirl', name:'Juicebox Girl', kind:'food',
    bio:'a cheerful juicebox with a bendy straw. always thirsty for a snack.',
    colors:{ body:0xE6537B, straw:0xFFFFFF },
    role:'customer', building:null, wholesome:true,
    lines:['*sip*', 'ooh what smells like toast?'],
    thanks:['*happy slurp*', 'yummy!']
  },
  ricebowl: {
    id:'ricebowl', name:'Rice Boi', kind:'food',
    bio:'a fierce bowl of rice with a topknot. a warrior at heart, hungry in practice.',
    colors:{ body:0xFFFFFF, bowl:0x2E479A },
    role:'customer', building:null, wholesome:true,
    lines:['i am Rice Boi.', 'a warrior must eat.'],
    thanks:['honor is served.', 'you fight well, chef.']
  },
  bananagirl: {
    id:'bananagirl', name:'Banana Girl', kind:'fruit',
    bio:'a stylish banana about town. keeps it classy.',
    colors:{ body:0xF2D23B },
    role:'customer', building:null, wholesome:true,
    lines:['love what you\'ve done with the place.', 'one toast, darling.'],
    thanks:['divine!', 'you\'re a treasure.']
  },

  // ---- GARY'S COMIC WORLD (appear in the comic shop / as lore) -----------
  spacedogs: {
    id:'spacedogs', name:'The Space Dogs', kind:'creature',
    bio:'the dog-pirate crew Gary runs with. rogues with a code.',
    colors:{ body:0x8C6239 },
    role:'cameo', building:'comics', wholesome:true,
    lines:['arf! (that means welcome aboard)', 'plunder awaits.']
  },
  al: {
    id:'al', name:'Al the Space Trucker', kind:'creature',
    bio:'an Elite Trucking driver. slobbish, boastful, and armed with a plasma cannon he fires indiscriminately.',
    colors:{ body:0x9AA0A6, cap:0x3D5FC0 },
    role:'cameo', building:'comics', wholesome:true,
    lines:['nobody out-hauls Al!', 'eat plasma, space rocks!', 'Jessica, i\'m fine — it\'s diet soda.']
  },
  garyvariants: {
    id:'garyvariants', name:'Gary\'s Kin', kind:'creature',
    bio:'purple, blue, and green cousins of Gary. the wider alien family.',
    colors:{ variants:[0x8E5AC8, 0x5A78C8, 0x5AC87F] },
    role:'cameo', building:'comics', wholesome:true,
    lines:['we come in peace (and comics).', 'family reunion!']
  },

  // ---- CROVIAN SYSTEM (Gary's home beyond Deep Space) ---------------------
  // The Dog Pirates are Robin Hood figures: they raid Elite Trucking and give the
  // spoils to poor worlds like BZ-943. Gary is on their side. The through-line
  // prop is the quadra-valve.
  slice: {
    id:'slice', name:'Slice', kind:'creature',
    bio:'captain of the Dog Pirates. decisive, hot-tempered, full of bad ideas that might just work.',
    colors:{ body:0x8C6239, bandana:0xE23B4E },
    role:'townsfolk', building:null, wholesome:true,
    lines:['all hands — i mean, all interns — on deck!', 'that idea is terrible. let\'s do it.', 'we redistribute crumbs around here.']
  },
  scruff: {
    id:'scruff', name:'Scruff Barkbeard', kind:'creature',
    bio:'a Dog Pirate grounded on Earth, loitering around the launch pad. Eyepatch, red bandana, endlessly hopeful about biscuits. He keeps the shore-side supply line running — if you need hull tape, you need Scruff.',
    colors:{ body:0x9A6A3C, bandana:0xE6537B },
    role:'townsfolk', building:null, wholesome:true,
    lines:[
      "Arrr, ye got biscuits? I don't bite... much.",
      "Elite Trucking bought every lane. We just... unbuy 'em.",
      "Tell Slice I want me old bunk back."
    ]
  },
  yango: {
    id:'yango', name:'Yango', kind:'creature',
    bio:'the Dog Pirates\' heavy. deadpan straight man.',
    colors:{ body:0x6E4A28, bandana:0x3D5FC0 },
    role:'townsfolk', building:null, wholesome:true,
    lines:['...yeah, that\'s a bad idea.', 'i lift things.', 'slice, no.']
  },
  chawar: {
    id:'chawar', name:'Chawar', kind:'creature',
    bio:'the Dog Pirates\' coward. permanently starving, obsessed with getting a Loco Burger.',
    colors:{ body:0xB07A44, bandana:0xF7C948 },
    role:'townsfolk', building:null, wholesome:true,
    lines:['i\'m too young to be raided!', 'has anyone seen a Loco Burger?', 'panic is a valid strategy.']
  },
  jessica: {
    id:'jessica', name:'Jessica', kind:'creature',
    bio:'Al\'s holographic AI companion. nags him about his health and deadlines.',
    colors:{ body:0x88CCFF, accent:0xFFFFFF },
    role:'townsfolk', building:null, wholesome:true,
    lines:['Al, your sodium level is concerning.', 'you\'re behind schedule. again.', 'please put down the plasma cannon.']
  },
  tito: {
    id:'tito', name:'Tito', kind:'creature',
    bio:'gruff shopkeeper of the BZ-943 farm-equipment emporium. keeps premium seed behind a force shield.',
    colors:{ body:0x9AA0A6, apron:0xD98C6A },
    role:'shopkeeper', building:null, wholesome:true,
    lines:['no window shopping.', 'i got one quadra-valve left. one.', 'credit? this is a farm shop, not a bank.']
  },

  // ---- DEEP CUTS (cameos / background) -----------------------------------
  lobsterguy: {
    id:'lobsterguy', name:'The Lobster', kind:'creature',
    bio:'a jacked lobster with expensive taste. a deep-lore fan favorite.',
    colors:{ body:0xE8461E },
    role:'cameo', building:null, wholesome:true,
    lines:['i want 200 jawnz.', '*flexes claw*']
  },
  gokuguy: {
    id:'gokuguy', name:'Goku Guy', kind:'human',
    bio:'a bearded fella in a training gi, forever mid-sprint. pure hype.',
    colors:{ gi:0xE08A1E, accent:0x5A78C8 },
    role:'cameo', building:'court', wholesome:true,
    lines:['gotta train!', 'power level rising!']
  },
  humans: {
    id:'humans', name:'The Regulars', kind:'human',
    bio:'a few human townsfolk who hang at the café. background crowd.',
    colors:{},
    role:'cameo', building:'cafe', wholesome:true,
    lines:['good crowd tonight.', 'pass the toast.']
  },

  // ---- BIG T'S GRILL (new building + minigame) ---------------------------
  bigt: {
    id:'bigt', name:'Big T', kind:'food',
    bio:'a big pink grilled porkchop with a charred crust edge. shades, a backwards blue cap, a gold "BIG" chain, and a bottle of "Bread Boi" BBQ sauce in hand. runs the grill and knows his way around a flip.',
    colors:{ body:0xC97F86, char:0x7A3B22, edge:0x5A2E1C, cap:0x35A7E0, chain:0xF7C948, sauce:0x9B2B1E },
    role:'shopkeeper', building:'grill', wholesome:true,
    lines:["low and slow, baby.", "you flippin' or you watchin'?", 'that\'s the good char right there.'],
    thanks:['now THAT\'s a flip!', 'you got the touch, kid.']
  },

  orangeguy: {
    id:'orangeguy', name:'Orange', kind:'fruit',
    bio:'a cheerful round orange with a leafy stem and a big friendly smile. sunny and easygoing.',
    colors:{ body:0xF39A1E, leaf:0x3FA34D, stem:0x6E4526 },
    role:'customer', building:null, wholesome:true,
    lines:['what a lovely day!', 'orange you glad to see me?'],
    thanks:['zesty! thank you!', '*happy citrus wiggle*']
  },
  bananabunch: {
    id:'bananabunch', name:'The Banana Bunch', kind:'fruit',
    bio:'a cheerful bunch of bananas and a single stick-legged banana pal. friendly folks from the banana lands.',
    colors:{ body:0xF2D23B, tips:0x6E4526 },
    role:'townsfolk', building:null, wholesome:true, hometown:'banana',
    lines:['bunch of us around here!', 'from the peel-lands, we are.', '*a-peeling wave*']
  },

  // ---- TOAST TOWN SPEEDWAY — AI rivals (wholesome, spawnable) -------------
  poppywheel: {
    id:'poppywheel', name:'Poppy Wheel', kind:'food',
    bio:'a hyperactive bagel with a need for speed. takes the racing line wide and late-brakes every corner.',
    colors:{ body:0xD9A441, accent:0xE23B4E },
    role:'townsfolk', building:null, wholesome:true, racer:true,
    lines:['eat my crumbs!', 'full throttle, full flavor!', 'that apex was MINE.']
  },
  ryerocket: {
    id:'ryerocket', name:'Rye Rocket', kind:'bread',
    bio:'a crusty sourdough in wraparound shades. smooth, conservative, rarely crashes.',
    colors:{ body:0x8C6239, accent:0x3D5FC0 },
    role:'townsfolk', building:null, wholesome:true, racer:true,
    lines:['slow is smooth, smooth is fast.', 'i rise at the finish.', 'crust never cracks under pressure.']
  },
  muffinmachine: {
    id:'muffinmachine', name:'Muffin Machine', kind:'food',
    bio:'a blueberry muffin who built her own spoiler out of a muffin tin. brilliant engineer, twitchy driver.',
    colors:{ body:0x6C86D4, accent:0xF7C948 },
    role:'townsfolk', building:null, wholesome:true, racer:true,
    lines:['downforce is just upward thinking.', 'my tin is tuned.', 'vroom muff-in!']
  },

  // ---- EXCLUDED FROM THE GAME (kept for the bible, never spawned) --------
  // Mature-themed / off-brand-for-public designs. wholesome:false = filtered.
  bananagirl_pinup: {
    id:'bananagirl_pinup', name:'Banana Girl (pin-up)', kind:'fruit',
    bio:'the mature pin-up version. excluded from the public game.',
    role:'cameo', building:null, wholesome:false, lines:[]
  },
  reeferz: {
    id:'reeferz', name:'Reeferz line', kind:'food',
    bio:'the cannabis vape branding (Blueberry Jam band, Sour Apple ad art). excluded from the game; the wholesome versions of these characters are used instead.',
    role:'cameo', building:null, wholesome:false, lines:[]
  },
  sadsmokiss: {
    id:'sadsmokiss', name:'Sad Smokiss', kind:'creature',
    bio:'the smoking heart-hoodie character. excluded from the public game.',
    role:'cameo', building:null, wholesome:false, lines:[]
  },
  buttlegs: {
    id:'buttlegs', name:'Butt-with-Legs', kind:'creature',
    bio:"Foot Man's friend. probably a bit much for a wholesome game — excluded.",
    role:'cameo', building:null, wholesome:false, lines:[]
  }
};

// ---- helpers ---------------------------------------------------------------
export const rosterList = () => Object.values(CHARACTERS);

// Only characters allowed to appear in the public game.
export const spawnable = () => rosterList().filter(c => c.wholesome);

export const byRole = (role) => spawnable().filter(c => c.role === role);

export const forBuilding = (buildingId) =>
  spawnable().filter(c => c.building === buildingId);

export const townsfolk = () => byRole('townsfolk');
export const customers = () => byRole('customer');
export const band = () => byRole('band');
export const shopkeeper = (buildingId) =>
  spawnable().find(c => c.role === 'shopkeeper' && c.building === buildingId);
